import { DisplaySettingsRounded } from '@mui/icons-material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Scrollbar } from 'app/components';
import config from 'app/config';
import { RetransmissionSettings, RetransmissionStatus, SettingsForm } from 'app/pages/Settings';
import { useAsyncAction } from 'app/services';
import { settingsLoad, settingsUpdate } from 'app/store/settings';
import { transportGroupLoad } from 'app/store/transport';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
const Settings = () => {
    const formRef = useRef(null);
    const { keys } = useSelector((state) => state.settings);
    const groupItems = useSelector((state) => state.transport.groupItems);
    const session = useSelector((state) => state.session.session);
    const [tab, setTab] = useState('main');
    const isSuperAdmin = (session === null || session === void 0 ? void 0 : session.role_id) === config.userRoles.SUPERADMIN;
    const { asyncAction: fetchData, isLoading: isFetchLoading, isSuccessful: isFetchSuccessful } = useAsyncAction(settingsLoad);
    const { asyncAction: fetchGroups, isLoading: isGroupsLoading } = useAsyncAction(transportGroupLoad);
    const { asyncAction: updateSubmit, isLoading: isUpdateLoading, isSuccessful: isUpdateSuccessful, isError: isUpdateError, } = useAsyncAction(settingsUpdate);
    const handleTabChange = (event, value) => {
        setTab(value);
    };
    useEffect(() => {
        fetchData();
        fetchGroups();
    }, []);
    const isLoading = isFetchLoading || isGroupsLoading;
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { direction: 'column', alignItems: 'center', sx: (theme) => ({
                maxWidth: tab === 'prefixes' || tab === 'status' ? '1200px' : '540px',
                height: '100%',
                margin: '0 auto',
                width: '100%',
                padding: theme.spacing(4, 2),
                transition: 'max-width 0.3s ease',
            }) },
            React.createElement(Stack, { direction: 'column', alignItems: 'center', sx: (theme) => ({
                    marginBottom: theme.spacing(3),
                    '.MuiAvatar-root': {
                        width: '56px',
                        height: '56px',
                        bgcolor: theme.palette.primary.main,
                    },
                    '.MuiAvatar-circular': {
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                    '.MuiTypography-root:first-of-type': {
                        marginTop: theme.spacing(1),
                    },
                }) },
                React.createElement(Avatar, null,
                    React.createElement(DisplaySettingsRounded, null)),
                React.createElement(Typography, { variant: 'body1' }, "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F")),
            React.createElement(Stack, { direction: 'column', alignItems: 'center', sx: (theme) => ({
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    border: '1px solid',
                    borderRadius: theme.shape.borderRadius,
                    borderColor: theme.palette.divider,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    '.MuiTabPanel-root': {
                        width: '100%',
                        height: '100%',
                        padding: 0,
                    },
                }) },
                !isLoading && !isFetchSuccessful && (React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: (theme) => ({
                        width: '100%',
                        height: '100%',
                        padding: theme.spacing(2),
                    }) },
                    React.createElement(Typography, { align: 'center', variant: 'body1', component: 'div' }, "\u041F\u0440\u043E\u0438\u0437\u043E\u0448\u043B\u0430 \u043E\u0448\u0438\u0431\u043A\u0430 \u043F\u0440\u0438 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F"),
                    React.createElement(Typography, { align: 'center', color: 'text.secondary', variant: 'caption', component: 'div' }, "\u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u043F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443 \u0438\u043B\u0438 \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u0432 \u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0443\u044E \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0443"))),
                (isLoading || isFetchSuccessful) && (React.createElement(TabContext, { value: tab },
                    React.createElement(TabList, { onChange: handleTabChange, TabIndicatorProps: {
                            children: React.createElement("span", null),
                        }, sx: (theme) => ({
                            width: '100%',
                            minHeight: '44px',
                            borderBottom: '1px solid',
                            borderColor: theme.palette.divider,
                            '.MuiTab-root': {
                                fontSize: '12px',
                                minHeight: '44px',
                                '&.Mui-selected': {
                                    color: 'inherit',
                                },
                            },
                            '.MuiTabs-indicator': {
                                display: 'flex',
                                justifyContent: 'center',
                                backgroundColor: 'transparent',
                            },
                            '.MuiTabs-indicator span': {
                                width: 'calc(100% - ' + theme.spacing(4) + ')',
                                bgcolor: theme.palette.primary.main,
                                borderRadius: theme.shape.borderRadius,
                            },
                        }) },
                        React.createElement(Tab, { value: 'main', label: '\u041E\u0441\u043D\u043E\u0432\u043D\u044B\u0435', disableRipple: true }),
                        React.createElement(Tab, { value: 'prefixes', label: '\u041F\u0440\u0435\u0444\u0438\u043A\u0441\u044B \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438', disableRipple: true }),
                        isSuperAdmin && React.createElement(Tab, { value: 'status', label: '\u0421\u0442\u0430\u0442\u0443\u0441 \u0440\u0435\u0442\u0440\u0430\u043D\u0441\u043B\u044F\u0446\u0438\u0438', disableRipple: true })),
                    React.createElement(TabPanel, { value: 'main' },
                        isLoading && (React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: {
                                width: '100%',
                                height: '100%',
                            } },
                            React.createElement(CircularProgress, { size: 24 }))),
                        !isLoading && isFetchSuccessful && (React.createElement(Stack, { direction: 'column', sx: {
                                width: '100%',
                                height: '100%',
                            } },
                            React.createElement(Stack, { direction: 'column', sx: (theme) => ({
                                    width: '100%',
                                    flex: 1,
                                    overflow: 'hidden',
                                    form: {
                                        padding: theme.spacing(2),
                                    },
                                }) },
                                React.createElement(Scrollbar, null,
                                    React.createElement(SettingsForm, { ref: formRef, defaultValues: keys, onSubmit: updateSubmit, groupItems: groupItems }))),
                            React.createElement(Stack, { direction: 'column', sx: (theme) => ({
                                    width: '100%',
                                    padding: theme.spacing(2),
                                    borderTop: '1px solid',
                                    borderColor: theme.palette.divider,
                                }) },
                                React.createElement(Stack, { direction: 'row', justifyContent: 'flex-end', sx: (theme) => ({
                                        width: '100%',
                                        '.MuiAlert-root': {
                                            width: '100%',
                                            marginBottom: theme.spacing(2),
                                            borderRadius: theme.shape.borderRadius,
                                        },
                                    }) },
                                    !isUpdateLoading && isUpdateError && (React.createElement(Alert, { severity: 'error' }, "\u041F\u0440\u043E\u0438\u0437\u043E\u0448\u043B\u0430 \u043E\u0448\u0438\u0431\u043A\u0430 \u043F\u0440\u0438 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0438 \u0434\u0430\u043D\u043D\u044B\u0445. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0441\u043D\u043E\u0432\u0430 \u0438\u043B\u0438 \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u0432 \u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0443\u044E \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0443.")),
                                    !isUpdateLoading && isUpdateSuccessful && React.createElement(Alert, { severity: 'success' }, "\u0414\u0430\u043D\u043D\u044B\u0435 \u0443\u0441\u043F\u0435\u0448\u043D\u043E \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u044B.")),
                                React.createElement(Stack, { direction: 'row', justifyContent: 'flex-end', sx: { width: '100%' } },
                                    React.createElement(LoadingButton, { variant: 'contained', loading: isUpdateLoading, onClick: () => { var _a; return (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.handleFormSubmit(); }, disableElevation: true }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F")))))),
                    React.createElement(TabPanel, { value: 'prefixes' },
                        isLoading && (React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: {
                                width: '100%',
                                height: '100%',
                            } },
                            React.createElement(CircularProgress, { size: 24 }))),
                        !isLoading && isFetchSuccessful && (React.createElement(Stack, { sx: (theme) => ({
                                width: '100%',
                                height: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                                padding: theme.spacing(2),
                            }) },
                            React.createElement(RetransmissionSettings, { groupItems: groupItems })))),
                    isSuperAdmin && (React.createElement(TabPanel, { value: 'status' },
                        isLoading && (React.createElement(Stack, { direction: 'column', alignItems: 'center', justifyContent: 'center', sx: {
                                width: '100%',
                                height: '100%',
                            } },
                            React.createElement(CircularProgress, { size: 24 }))),
                        !isLoading && isFetchSuccessful && (React.createElement(Stack, { sx: (theme) => ({
                                width: '100%',
                                height: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                                padding: theme.spacing(2),
                            }) },
                            React.createElement(RetransmissionStatus, { groupItems: groupItems })))))))))));
};
export default Settings;
